import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
    CheckCircle, Circle, Clock, ForkKnife, Bell, Package,
    House, ArrowsClockwise, Receipt, Copy, Share, BellRinging, Printer,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import Header from "@/components/site/Header";
import { useI18n } from "@/context/I18nContext";
import { api } from "@/lib/api";
import { subscribePush, pushSupported } from "@/lib/push";
import printReceipt from "@/lib/printReceipt";

const POLL_MS = 10_000;

/**
 * Public order tracking page. The order ID itself is the access token —
 * customers receive their link after checkout (Stripe success page or POS
 * receipt). No login required, but only the canonical status fields are
 * exposed. Auto-refreshes every 10 seconds so the customer doesn't need
 * to reload the tab.
 */
export default function TrackOrder() {
    const { orderId } = useParams();
    const { lang } = useI18n();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    useEffect(() => {
        let alive = true;
        const load = async () => {
            try {
                const r = await api.get(`/orders/${orderId}/track`);
                if (!alive) return;
                setOrder(r.data);
                setErr("");
            } catch (e) {
                if (!alive) return;
                setErr(e?.response?.status === 404 ? "not_found" : "error");
            } finally {
                if (alive) setLoading(false);
            }
        };
        load();
        const id = setInterval(load, POLL_MS);
        return () => { alive = false; clearInterval(id); };
    }, [orderId]);

    const copyLink = () => {
        navigator.clipboard?.writeText(window.location.href);
        toast.success(lang === "es" ? "Enlace copiado" : "Link copied");
    };

    const share = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: "J&W Deli order",
                    text: lang === "es" ? "Sigue mi pedido" : "Track my order",
                    url: window.location.href,
                });
            } catch { /* user cancelled */ }
        } else {
            copyLink();
        }
    };

    return (
        <>
            <Header />
            <main className="min-h-screen bg-muted pt-24 md:pt-28 pb-16" data-testid="track-page">
                <div className="mx-auto w-full max-w-2xl px-4 md:px-8">
                    {loading ? (
                        <SkeletonCard />
                    ) : err === "not_found" ? (
                        <NotFoundCard />
                    ) : err ? (
                        <p className="text-center text-destructive">Error loading order. Refresh.</p>
                    ) : (
                        <OrderCard
                            order={order}
                            onCopy={copyLink}
                            onShare={share}
                            es={lang === "es"}
                        />
                    )}

                    <div className="text-center mt-6">
                        <Link
                            to="/"
                            data-testid="track-back"
                            className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-muted-foreground hover:text-foreground"
                        >
                            <House size={14} weight="bold" />
                            {lang === "es" ? "Volver al inicio" : "Back home"}
                        </Link>
                    </div>
                </div>
            </main>
        </>
    );
}

function OrderCard({ order, onCopy, onShare, es }) {
    const status = order.status || "pending";
    const isDelivery = order.fulfillment === "delivery";
    const idShort = (order.id || "").slice(-8).toUpperCase();
    const [pushState, setPushState] = useState(() => {
        if (!pushSupported()) return "unsupported";
        return Notification.permission === "granted" ? "on" : "off";
    });

    // Auto-subscribe in the background once we have the order ID — silent if
    // permission is "default" (we'll show the button instead).
    useEffect(() => {
        if (!pushSupported() || Notification.permission !== "granted") return;
        subscribePush({ orderId: order.id }).then((ok) => {
            if (ok) setPushState("on");
        });
    }, [order.id]);

    const enablePush = async () => {
        const ok = await subscribePush({ orderId: order.id });
        if (ok) {
            setPushState("on");
            toast.success(es ? "Notificaciones activadas" : "Notifications enabled");
        } else {
            toast.error(es ? "No se pudieron activar" : "Couldn't enable notifications");
        }
    };

    // Step labels — the last step differs based on pickup vs delivery
    const steps = useMemo(() => [
        {
            id: "pending",
            icon: Receipt,
            label_en: "Received",
            label_es: "Recibido",
            desc_en: "We got your order",
            desc_es: "Recibimos tu pedido",
        },
        {
            id: "preparing",
            icon: ForkKnife,
            label_en: "Cooking",
            label_es: "Preparando",
            desc_en: "Our kitchen is on it",
            desc_es: "La cocina lo está preparando",
        },
        {
            id: "ready",
            icon: Bell,
            label_en: isDelivery ? "Out for delivery" : "Ready for pickup",
            label_es: isDelivery ? "En camino" : "Listo para recoger",
            desc_en: isDelivery
                ? "Your food is on the way"
                : "Come pick it up at our counter",
            desc_es: isDelivery
                ? "Tu comida va en camino"
                : "Pasa por el mostrador a recogerlo",
        },
        {
            id: "delivered",
            icon: Package,
            label_en: isDelivery ? "Delivered" : "Picked up",
            label_es: isDelivery ? "Entregado" : "Recogido",
            desc_en: es ? "" : "Enjoy your meal!",
            desc_es: "Buen provecho.",
        },
    ], [isDelivery, es]);

    const order_status = ["pending", "preparing", "ready", "delivered"];
    const currentIdx = Math.max(0, order_status.indexOf(status));
    const isDone = currentIdx === order_status.length - 1;

    return (
        <>
            <div className="bg-white rounded-3xl shadow-sm border border-border overflow-hidden">
                <div className="bg-foreground text-background px-6 py-5 flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <p className="text-[10px] uppercase tracking-[0.25em] opacity-70">
                            {es ? "Tu pedido" : "Your order"}
                        </p>
                        <p className="font-display font-black text-2xl mt-0.5">
                            #{idShort}
                        </p>
                    </div>
                    <span
                        data-testid="track-status-pill"
                        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest ${
                            isDone ? "bg-emerald-500 text-white" : "bg-white/15"
                        }`}
                    >
                        <span className={`h-2 w-2 rounded-full ${isDone ? "bg-white" : "bg-orange-400 animate-pulse"}`} />
                        {steps[currentIdx][es ? "label_es" : "label_en"]}
                    </span>
                </div>

                <div className="px-6 py-6">
                    {/* Timeline */}
                    <ol className="relative space-y-5">
                        {steps.map((s, i) => {
                            const reached = i <= currentIdx;
                            const current = i === currentIdx;
                            const Icon = reached ? s.icon : Circle;
                            return (
                                <li
                                    key={s.id}
                                    data-testid={`step-${s.id}`}
                                    className="flex items-start gap-4"
                                >
                                    <div
                                        className={`relative z-10 grid place-items-center h-12 w-12 rounded-full shrink-0 transition-all ${
                                            current && isDone
                                                ? "bg-emerald-500 text-white shadow-lg shadow-emerald-500/30"
                                                : current
                                                ? "bg-primary text-primary-foreground shadow-lg shadow-primary/30"
                                                : reached
                                                ? "bg-emerald-500 text-white"
                                                : "bg-muted text-muted-foreground"
                                        }`}
                                    >
                                        {current && (
                                            <span className={`absolute inset-0 rounded-full animate-ping opacity-40 ${isDone ? "bg-emerald-500" : "bg-primary"}`} />
                                        )}
                                        {reached
                                            ? <CheckCircle size={22} weight="fill" />
                                            : <Icon size={20} weight="bold" />
                                        }
                                    </div>
                                    <div className="pt-2 flex-1">
                                        <p
                                            className={`font-display font-black text-lg leading-none ${
                                                reached ? "text-foreground" : "text-muted-foreground"
                                            }`}
                                        >
                                            {s[es ? "label_es" : "label_en"]}
                                        </p>
                                        <p
                                            className={`text-sm mt-1 ${
                                                current
                                                    ? "text-foreground"
                                                    : "text-muted-foreground"
                                            }`}
                                        >
                                            {s[es ? "desc_es" : "desc_en"]}
                                        </p>
                                    </div>
                                </li>
                            );
                        })}
                        {/* Connector line */}
                        <div
                            aria-hidden
                            className="absolute left-6 top-6 bottom-6 w-px bg-border -z-0"
                        />
                    </ol>

                    {/* Driver / delivery info — shown only when assigned */}
                    {order.driver_name && (
                        <div
                            data-testid="track-driver"
                            className="mt-6 rounded-2xl border border-primary/30 bg-primary/5 p-4"
                        >
                            <div className="flex items-center gap-3">
                                <span className="grid place-items-center h-12 w-12 rounded-full bg-primary text-primary-foreground">
                                    <ForkKnife size={22} weight="fill" />
                                </span>
                                <div className="flex-1">
                                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
                                        {es ? "Tu repartidor" : "Your driver"}
                                    </p>
                                    <p className="font-display font-black text-lg leading-none">
                                        {order.driver_name}
                                    </p>
                                </div>
                                {order.eta_minutes && !order.delivered_at && (
                                    <div className="text-right">
                                        <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
                                            ETA
                                        </p>
                                        <p className="font-display font-black text-2xl leading-none text-primary">
                                            {order.eta_minutes}<span className="text-xs ml-0.5">min</span>
                                        </p>
                                    </div>
                                )}
                            </div>
                            {order.picked_up_at && !order.delivered_at && (
                                <p className="mt-3 text-xs text-center font-bold uppercase tracking-widest text-amber-700 bg-amber-50 rounded-full py-1">
                                    {es ? "Recogido — en camino" : "Picked up — on the way"}
                                </p>
                            )}
                        </div>
                    )}

                    {/* Items summary */}
                    {order.items?.length > 0 && (
                        <div className="mt-8 pt-6 border-t border-border">
                            <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-3">
                                {es ? "Tu pedido" : "Order items"}
                            </p>
                            <ul className="space-y-1.5 text-sm">
                                {order.items.map((it, i) => (
                                    <li key={i} className="flex justify-between">
                                        <span>
                                            <span className="font-bold text-primary mr-2">
                                                {it.qty}×
                                            </span>
                                            {it.name}
                                        </span>
                                        <span className="font-mono text-muted-foreground">
                                            ${((it.price || 0) * (it.qty || 1)).toFixed(2)}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                            <div className="mt-4 pt-3 border-t border-border space-y-1 text-sm">
                                <Row k={es ? "Subtotal" : "Subtotal"} v={`$${(order.subtotal || 0).toFixed(2)}`} />
                                {order.delivery_fee > 0 && (
                                    <Row k={es ? "Envío" : "Delivery"} v={`$${order.delivery_fee.toFixed(2)}`} />
                                )}
                                <Row k={es ? "Impuesto" : "Tax"} v={`$${(order.tax || 0).toFixed(2)}`} />
                                <div className="flex justify-between font-display font-black text-xl pt-2 border-t border-border">
                                    <span>Total</span>
                                    <span>${(order.total || 0).toFixed(2)}</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Live indicator */}
                    <div className="mt-6 flex items-center justify-center gap-2 text-xs text-muted-foreground">
                        <ArrowsClockwise size={14} weight="bold" className="animate-spin-slow" />
                        {es
                            ? "Esta página se actualiza sola cada 10s"
                            : "This page auto-refreshes every 10s"}
                    </div>
                </div>
            </div>

            {/* Share + copy + notifications */}
            <div className="flex flex-wrap gap-3 mt-4 justify-center">
                {pushState !== "unsupported" && pushState !== "on" && (
                    <button
                        data-testid="track-enable-push"
                        onClick={enablePush}
                        className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-4 py-2.5 text-xs font-bold uppercase tracking-widest hover:bg-primary/90"
                    >
                        <BellRinging size={14} weight="fill" />
                        {es ? "Avísame cuando esté listo" : "Notify me when ready"}
                    </button>
                )}
                {pushState === "on" && (
                    <span
                        data-testid="track-push-on"
                        className="inline-flex items-center gap-2 rounded-full bg-emerald-100 text-emerald-800 px-4 py-2.5 text-xs font-bold uppercase tracking-widest"
                    >
                        <BellRinging size={14} weight="fill" />
                        {es ? "Notificaciones activadas" : "Notifications on"}
                    </span>
                )}
                <button
                    data-testid="track-copy"
                    onClick={onCopy}
                    className="inline-flex items-center gap-2 rounded-full bg-white border border-border text-foreground px-4 py-2.5 text-xs font-bold uppercase tracking-widest hover:bg-muted"
                >
                    <Copy size={14} weight="bold" /> {es ? "Copiar enlace" : "Copy link"}
                </button>
                <button
                    data-testid="track-share"
                    onClick={onShare}
                    className="inline-flex items-center gap-2 rounded-full bg-foreground text-background px-4 py-2.5 text-xs font-bold uppercase tracking-widest hover:opacity-90"
                >
                    <Share size={14} weight="bold" /> {es ? "Compartir" : "Share"}
                </button>
                <button
                    data-testid="track-print"
                    onClick={() => printReceipt(order, { kind: "customer" })}
                    className="inline-flex items-center gap-2 rounded-full bg-white border border-border text-foreground px-4 py-2.5 text-xs font-bold uppercase tracking-widest hover:bg-muted"
                >
                    <Printer size={14} weight="bold" /> {es ? "Imprimir" : "Print"}
                </button>
            </div>
        </>
    );
}

function Row({ k, v }) {
    return (
        <div className="flex justify-between">
            <span className="text-muted-foreground">{k}</span>
            <span className="font-bold">{v}</span>
        </div>
    );
}

function SkeletonCard() {
    return (
        <div className="bg-white rounded-3xl border border-border p-8 animate-pulse" data-testid="track-loading">
            <div className="h-8 bg-muted rounded w-1/3 mb-6" />
            <div className="space-y-4">
                {[0, 1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center gap-4">
                        <div className="h-12 w-12 rounded-full bg-muted" />
                        <div className="flex-1 space-y-2">
                            <div className="h-3 bg-muted rounded w-1/4" />
                            <div className="h-3 bg-muted rounded w-1/2" />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function NotFoundCard() {
    return (
        <div className="bg-white rounded-3xl border border-border p-10 text-center" data-testid="track-404">
            <Clock size={48} weight="bold" className="text-muted-foreground mx-auto" />
            <h2 className="font-display font-black text-2xl mt-4">
                Order not found
            </h2>
            <p className="text-muted-foreground mt-2 text-sm">
                The link may be incorrect or this order has been removed.
            </p>
        </div>
    );
}
